import * as _ from 'lodash';
import * as api from './../api/api.service';

import { DataSet, Puck } from './impact.type';

export default function BuyerImpactService(
  $log,
  $q,
  $http,
  jsendHttpInterceptor,
  colors,
  api: api.IApiService
) {
  'use strict';
  'ngInject';

  // var aptitudePositiveColor = colors.aptGreen;
  var aptitudeNegativeColor = colors.aptOrange;

  return {
    getApplicationPermissions: function (context, entityId, supplementalId) {
      return $http.get(
        '../ApplicationPermissionsServiceController/getApplicationPermissions',
        {
          intercept: true,
          params: {
            context,
            supplementalId,
            entityId
          }
        }
      );
    },

    getRfpDetails: function (rfpId) {
      return api.rfps.getRfp(rfpId, true);
    },

    // Exposed only for testing and not intended for direct calls.
    _getPricingComparison: function (
      rfpId,
      sellerId,
      partnerId,
      priceType,
      duration
    ) {
      return api.rfps.buyers.getPricingComparison(
        rfpId,
        sellerId,
        duration,
        priceType,
        partnerId
      );
    },

    _updateBenchmarksGraphWithBenchmarkRanges: function (target, results) {
      target.benchmarksGraph.data.benchmarks.all.range = 'unavailable';
      target.benchmarksGraph.data.benchmarks.similar.range = 'unavailable';
    },

    //////////////////
    // Buyer Functions
    //////////////////

    // This function is not intended to be called by outside code (hence the naming). It's exposed only for testing.
    // It gets the initial set of details for each puck (things like savings and market share) but doesn't drill down
    // to get micro-chart data or list of facilities.
    _getPuckDetailsForBuyer: function (dataSets, masterPlanId, memberId) {
      return api.buyers
        .getMemberImpact(memberId, masterPlanId)
        .then(function (results) {
          // This is a horrific kludge to make testing this code work when there's no jSendHttpInterceptor in place.
          if (results['data'] && results['data']['data']) {
            results = results['data']['data'];
          }

          // Go through all of the seller info returned and get the puck for this member for each data set. Then add the
          // details to that puck.
          _.each(results.sellerImpact, (puckDetails) => {
            // Use the returned puck data to find the specific puck to which we need to add details.
            var targetDataSet: any = _.find(dataSets, {
              id: puckDetails.sellerId
            });
            var targetPuck: any = _.find(targetDataSet.pucks, {
              id: results.memberId
            });

            targetDataSet.overview.marketShare =
              targetDataSet.overview.targetPotentialMarketShare;
            targetDataSet.overview.duration = {
              priceType: puckDetails.priceType,
              duration: puckDetails.duration
            };

            // Note: This particular piece of data is at the higher level of the returned data, so we don't pluck it
            // from the array we're iterating on at this moment.
            targetPuck.memberPlanId = results.memberPlanId;

            targetPuck.continuedSpend = puckDetails.continuedSpend;
            targetPuck.additionalValue = puckDetails.additionalValue;

            targetPuck.plannedSpend = puckDetails.plannedSpend;
            targetPuck.potentialSpend = puckDetails.potentialSpend;

            targetPuck.savings = puckDetails.savings;
            targetPuck.savingsPercentage = puckDetails.plannedSpend
              ? Math.round(
                  (puckDetails.savings / puckDetails.continuedSpend) * 100.0
                )
              : 0;
            targetPuck.potentialMarketSharePercentage = puckDetails.potentialSpend
              ? Math.round(
                  (puckDetails.plannedSpend / puckDetails.potentialSpend) *
                    100.0
                )
              : 0;

            targetPuck.allocatedMarketShare = puckDetails.marketShare;
            targetPuck.targetMarketShare = puckDetails.targetMarketShare;
            targetPuck.meetsTargetMarketShare =
              puckDetails.meetsTargetMarketShare;

            targetPuck.continuedBenchmarkAll =
              puckDetails.continuedBenchmarkAll;
            targetPuck.plannedBenchmarkAll = puckDetails.plannedBenchmarkAll;
            targetPuck.continuedBenchmarkLikeMe =
              puckDetails.continuedBenchmarkLikeMe;
            targetPuck.plannedBenchmarkLikeMe =
              puckDetails.plannedBenchmarkLikeMe;

            targetPuck.conversionsNeededCount =
              puckDetails.conversionsNeededCount;
            targetPuck.status = puckDetails.inclusionStatus;
            targetPuck.inclusionModifiedBy = puckDetails.inclusionModifiedBy;

            targetPuck.duration = puckDetails.duration;
            targetPuck.priceType = puckDetails.priceType;

            targetPuck.savingsDisplay = {
              savingsAmount: puckDetails.savings
            };

            targetPuck.benchmarksGraph = {
              data: {
                benchmarks: {
                  all: {
                    currentRanking:
                      puckDetails.spendBenchmark.continuedAllBuyersBenchmark,
                    plannedRanking:
                      puckDetails.spendBenchmark.plannedAllBuyersBenchmark,
                    range: 'loading' // loading, loaded, unavailable
                  },
                  similar: {
                    currentRanking:
                      puckDetails.spendBenchmark
                        .continuedSimilarBuyersBenchmark,
                    plannedRanking:
                      puckDetails.spendBenchmark.plannedSimilarBuyersBenchmark,
                    range: 'loading' // loading, loaded, unavailable
                  }
                },
                savingsAmount:
                  puckDetails.continuedSpend +
                  puckDetails.additionalValue -
                  puckDetails.plannedSpend
              },
              config: {
                benchmarkType: 'similar',
                templateName: 'group-impact-details'
              }
            };

            // These get filled in only when we show the details for a given puck.
            targetPuck.facilities = [];
            targetPuck.marketShare = {};
          });

          return true;
        });
    },

    getMasterPlanId: function (purchasePlanId) {
      return $http.get(
        '/aptitude/api/purchaseplans/' + purchasePlanId + '/masterPlanId'
      );
    },

    getDataSetsForBuyer: function (masterPlanId) {
      var self = this;
      var retVal: any = {
        // masterPlanPromise: null,
        // puckDetailsPromise: null
      };
      // Reformat the returned data into the data set format the page now uses.
      var dataSets: any[] = [];

      retVal.masterPlanPromise = $http
        .get('/aptitude/buyer/GroupImpactServiceController/getMasterPlanInfo', {
          intercept: true,
          params: {
            masterPlanId: masterPlanId
          }
        })
        .then(function (results) {
          // This is a horrific kludge to make testing this code work when there's no jSendHttpInterceptor in place. For
          // example, in unit test code today the interceptor is not in place.
          if (results.data && results.data.data) {
            results = results.data.data;
          }

          // We would like the sellers to always appear in the same order so this will sort them alphabetically.
          results.sellerInfo = _.sortBy(results.sellerInfo, function (
            seller: any
          ) {
            return seller.sellerName;
          });

          _.each(results.sellerInfo, (seller: any) => {
            var pucks: any[] = [];
            var dataSet = {
              name: seller.sellerName,
              id: seller.sellerId,
              aggregationGroupName: results.aggregationGroupName,
              categoryName: results.categoryName,
              categoryId: results.categoryId,
              requestId: results.requestId,
              rfpType: results.rfpType,
              pucks: pucks,
              overview: {
                notSavingCount: 0,

                totalSpend: 0,
                totalPotentialSpend: 0,
                targetPotentialMarketShare: seller.targetMarketShare,

                underCommittedCount: 0,

                inCount: 0,
                inSavings: 0,

                undecidedCount: 0,
                undecidedSavings: 0,

                outCount: 0,
                outSavings: 0,

                totalCount: 0,
                totalSavings: 0,

                gauges: {
                  in: 0,
                  undecided: 0,
                  out: 0
                },
                dials: {
                  notSaving: {
                    percentage: 0,
                    number: 0,
                    color: aptitudeNegativeColor
                  },
                  potentialMarketShare: {
                    percentage: 0,
                    number: 0,
                    numberIsPercentage: true,
                    label: '',
                    color: aptitudeNegativeColor
                  },
                  underCommitted: {
                    percentage: 0,
                    number: 0,
                    label: 'Systems',
                    color: aptitudeNegativeColor
                  }
                }
              }
            };

            // Create pucks for all of the systems.
            _.each(results.memberImpact, (member: any) => {
              var puck = {
                name: member.memberName,
                id: member.memberId
              };

              dataSet.pucks.push(puck);
            });

            dataSets.push(dataSet);
          });

          // Now we return the data to satisfy the promise we gave.
          return dataSets;
        });

      var deferred = $q.defer();
      var puckDetailsPromises: any[] = [];

      retVal.puckDetailsPromise = deferred.promise;

      // Create pucks for all of the systems.
      retVal.masterPlanPromise.then(function () {
        _.each(dataSets[0].pucks, function (puck: Puck) {
          // Kick off a call to get detail data for this puck and save off the promise for this one with all the others.
          var tempPromise = self._getPuckDetailsForBuyer(
            dataSets,
            masterPlanId,
            puck.id
          );
          tempPromise.then(function () {
            deferred.notify('puck details!');
          });
          puckDetailsPromises.push(tempPromise);
        });

        // Make a single promise from all the promises to get puck details for each puck. This one will fire only
        // once they're all done.
        $q.all(puckDetailsPromises).then(function () {
          deferred.resolve();
        });
      });

      // Return an object containing promises for both events we've kicked off: getting the master plan info and getting
      // the puck info for all pucks.
      return retVal;
    },

    // If we haven't already gotten the fine details for a given puck, this will go get them. That means calls to get
    // a complete list of facilities and micro-chart info.
    getPuckDetailsForBuyer: function (
      puck,
      memberId,
      categoryId,
      rfpId,
      sellerId,
      priceType,
      duration,
      marketShare
    ) {
      var target = puck;
      var self = this;

      if (!puck.fineDetailsLoaded) {
        puck.fineDetailsLoaded = true;

        // Ask for facilities info for this particular puck.
        var facilitiesPromise = api.buyers
          .getFacilities(memberId, categoryId, rfpId, sellerId)
          .then(function (facilities) {
            // The data comes back with the system at a level above all its facilities. This flattens it out.
            // TODO: Should this be moved to the api service or do we ever want the top level?
            var parent = facilities[0];

            target.facilities = facilities.concat(parent.children);

            // Then we sort the list.
            target.facilities = _.sortBy(target.facilities, 'name');

            // Get rid of null values for spend.
            parent.categorySpend.last12monthSpend =
              parent.categorySpend.last12monthSpend || 0;

            _.each(parent.children, function (child) {
              child.categorySpend.last12monthSpend =
                child.categorySpend.last12monthSpend || 0;
            });
          });

        // Ask for pricing comparison data for this particular puck.
        var pricingComparisonPromise = this._getPricingComparison(
          rfpId,
          sellerId,
          memberId,
          priceType,
          duration
        ).then(function (pricingComparison) {
          target.microChartData = pricingComparison;
        });

        // Kick off yet another call to get the benchmark ranges we need for the modal macro-chart.
        var pricingBenchmarksPromise = api.rfps.sellers
          .getPricingBenchmarks(
            rfpId,
            sellerId,
            memberId,
            priceType,
            duration,
            marketShare
          )
          .then(function (results) {
            // Set the range indicators to let external code know whether we were able to get benchmark numbers or not
            // for each type of range.
            self._updateBenchmarksGraphWithBenchmarkRanges(puck, results);
          });

        return $q.all([
          facilitiesPromise,
          pricingComparisonPromise,
          pricingBenchmarksPromise
        ]);
      } else {
        // We've already loaded the fine details for this puck.
        return $q.when(true);
      }
    },

    setAllSystemsToIn: function (masterPlanId, sellerId, included) {
      // Needs to be replaced once BEND-488 gives us a new API to call.
      return $http.post(
        '../buyer/GroupImpactServiceController/setInclusionFlagAllMembers',
        { masterPlanId: masterPlanId, sellerId: sellerId, included: included },
        { intercept: true, postEncoding: 'legacy' }
      );
    },

    setSystemStatus: function (masterPlanId, memberId, sellerId, included) {
      // Needs to be replaced once BEND-488 gives us a new API to call.
      return $http.post(
        '../buyer/GroupImpactServiceController/setInclusionFlag',
        {
          masterPlanId: masterPlanId,
          memberId: memberId,
          sellerId: sellerId,
          included: included
        },
        { intercept: true, postEncoding: 'legacy' }
      );
    }
  };
}
